import type { CSSProperties } from 'react';
import type { PublicAssetShareLook } from 'fym-shared/src/music24/assetShares';
import {
  ACCENT_MIN_CONTRAST,
  TEXT_MIN_CONTRAST,
  contrastRatio,
  hexToOklch,
  oklchToHex,
  readableOn,
} from 'fym-shared/src/music24/releaseThemes';
import type { Oklch } from 'fym-shared/src/music24/releaseThemes';
import type { SMLTheme } from '../theme/smlTheme';

/** The ground of the dark theater page (design asset-share c, `.c-dk`). */
export const SHARE_DARK_GROUND = '#050507';
/** The ground of the light theater page: the light `--bg-0` token. */
export const SHARE_LIGHT_GROUND = '#f3f2f8';
/** The Music24 violet: the accent of a share with no look. */
export const SHARE_FALLBACK_ACCENT = '#6417fb';
const WHITE = '#ffffff';
const INK = '#0b0a10';

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const FIT_MAX_STEPS = 40;
const FIT_STEP = 0.02;

/** The colours of the artist that the share page wears. */
export interface ShareArtColours {
  /** The fill of the primary action ("Listen to <song>"), the progress bar, the dot. */
  accent: string;
  /** The text on the accent. */
  onAccent: string;
  /** The ground of the letterbox around a tall video. */
  ground: string;
}

/** Moves the lightness of `hex` in `direction` until the contrast on `ground` is at least `minimum`. Pure. */
function fitOn(hex: string, ground: string, minimum: number, direction: 1 | -1): string {
  let colour: Oklch = hexToOklch(hex);
  let fitted = hex;
  let steps = 0;
  while (contrastRatio(fitted, ground) < minimum && steps < FIT_MAX_STEPS) {
    colour = { L: Math.max(0, Math.min(1, colour.L + direction * FIT_STEP)), C: colour.C * 0.98, H: colour.H };
    fitted = oklchToHex(colour);
    steps += 1;
  }
  return fitted;
}

/**
 * The artist colour of the share page from `PublicAssetShare.look.colours`. No look (or a colour
 * that is not `#rrggbb`) gives the Music24 violet. In the light theme the accent gets darker until it
 * has 4.5:1 on the light ground (design asset-share notes). In the dark theme it gets lighter until
 * it has 3:1 on the dark ground. When the accent changes, the text on it is white or ink, the one
 * with the higher contrast. Pure.
 */
export function shareArtColours(look: PublicAssetShareLook | null, theme: SMLTheme): ShareArtColours {
  const colours = look?.colours;
  const valid = colours !== undefined && HEX_PATTERN.test(colours.accent) && HEX_PATTERN.test(colours.onAccent);
  if (!valid) {
    return { accent: SHARE_FALLBACK_ACCENT, onAccent: WHITE, ground: SHARE_DARK_GROUND };
  }
  const ground = HEX_PATTERN.test(colours.background) ? colours.background : SHARE_DARK_GROUND;
  const accent =
    theme === 'light'
      ? fitOn(colours.accent, SHARE_LIGHT_GROUND, TEXT_MIN_CONTRAST, -1)
      : fitOn(colours.accent, SHARE_DARK_GROUND, ACCENT_MIN_CONTRAST, 1);
  const onAccent = accent === colours.accent ? colours.onAccent : readableOn(accent, [WHITE, INK]);
  return { accent, onAccent, ground };
}

/** The CSS variables of the artist colour, for the root element of the page. */
export function shareArtStyle(colours: ShareArtColours): CSSProperties {
  return {
    '--art': colours.accent,
    '--art-ink': colours.onAccent,
    '--art-ground': colours.ground,
  } as CSSProperties;
}
